import React, { useState } from 'react';

import { Box, Tab, Tabs } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { useAnalytics, useAuth, useMounted } from 'hooks';
import { useRouter } from 'next/router';
import {
  useCollectionById,
  useExternalGameUrl,
  useFriendsPlaying,
  useJumpBackIn,
  useSubscriptions,
} from 'queries';
import { FormattedMessage, useIntl } from 'react-intl';
import SwipeableViews from 'react-swipeable-views';
import { Routes } from 'routes';

import { canPlayGame, isExternalGameProvider } from 'utils/game.utils';
import { mapGameDetails } from 'utils/gameDetails/game.utils';
import { generatePath } from 'utils/routes.utils';

import { Events } from 'constants/events';
import { Game, TypeOfGame } from 'services/api/definitions/game.definitions';

import FriendLoading from 'components/@friends/FriendLoading';
import About from 'components/@game-detail/About';
import Friends from 'components/@game-detail/Friends';
import GamblingWarning from 'components/@game-detail/GamblingWarning';
import Leaderboard from 'components/@game-detail/Leaderboard';
import LeaderboardLoading from 'components/@game-detail/Leaderboard/LeaderboardLoading';
import NotLoggedInCta from 'components/@home/NotLoggedInCta';
import TabPanel from 'components/@tabs/TabPanel';

import GameDetailButtons from './GameDetailButtons';

interface Props {
  game: Game;
  collectionId: string;
}

const GameDetail = ({ game, collectionId }: Props) => {
  const intl = useIntl();
  const isMounted = useMounted();
  const router = useRouter();
  const { isLoggedIn, user } = useAuth();
  const { send } = useAnalytics();

  const { isSubscribedTo, isLoading: isLoadingSubscriptions } = useSubscriptions({
    enabled: isExternalGameProvider(game.provider),
  });
  const { friends, isLoading: isLoadingFriends } = useFriendsPlaying(game.id);
  const { jumpBackIn, isLoading: isLoadingJumpBackIn } = useJumpBackIn();
  const { collection } = useCollectionById(collectionId);

  const [activeTabIndex, setActiveTabIndex] = useState<number>();
  const [fetchExternalUrl, setFetchExternalUrl] = useState(false);

  const path = generatePath(Routes.game, { slug: game.slugName });
  const { isLoading, isError } = useExternalGameUrl(game.id, game.webviewRequiresToken ?? false, {
    enabled: fetchExternalUrl,
    onSuccess: url => {
      if (!!url) router.push(path);
    },
  });

  const userPlayedTheGame = jumpBackIn ? !!jumpBackIn.find(g => g.id === game.id) : undefined;
  const loadingTabs = !isMounted || (isLoggedIn && isLoadingJumpBackIn);
  const usedTabIndex = activeTabIndex ?? (userPlayedTheGame && game.leaderboard ? 0 : 1);

  const handleTabChange = (_event: React.ChangeEvent<{}>, newValue: number) => {
    setActiveTabIndex(newValue);
  };

  const handleTabChangeIndex = (index: number) => {
    setActiveTabIndex(index);
  };

  const handlePlayClick = async () => {
    send({
      event: Events.gamePlayed,
      data: { game: { slug: game?.slugName } },
      withUser: true,
    });

    if (game.type === TypeOfGame.webview) {
      setFetchExternalUrl(true);
    } else {
      router.push(path);
    }
  };

  return (
    <>
      <Box display="flex" flexDirection="column" mb={2}>
        <Box mx={4} mb={2}>
          {canPlayGame(game) && (
            <GameDetailButtons
              isLoadingSubscriptions={isLoadingSubscriptions}
              isLoadingUrl={isLoading}
              isSubscribedTo={isSubscribedTo}
              isErrorUrl={isError}
              game={game}
              handlePlayClick={handlePlayClick}
            />
          )}
          <NotLoggedInCta
            title={intl.formatMessage({ id: 'games.login.title' })}
            description={intl.formatMessage({ id: 'games.login.body' })}
            titleVariant="h6"
            bgcolor="background.paper"
            p={4}
            borderRadius={4}
            my={6}
          />
        </Box>
      </Box>
      <Box m={4}>
        {isLoggedIn && user && !isLoadingFriends && (
          <Friends friends={friends} avatar={user.avatar} shareTitle={game.name} />
        )}
        {(!isMounted || isLoadingFriends) && <FriendLoading height={36} />}
      </Box>
      {game.isGameOfChance && (
        <Box m={4}>
          <GamblingWarning />
        </Box>
      )}
      {loadingTabs ? (
        <>
          <nav>
            <Tabs value={false} onChange={handleTabChange} variant="fullWidth">
              <Tab label={<Skeleton width={120} height={22} />} id="tab-0-loading" disabled />
              <Tab label={<Skeleton width={120} height={22} />} id="tab-1-loading" disabled />
            </Tabs>
            <Box my={6}>
              <LeaderboardLoading />
            </Box>
          </nav>
        </>
      ) : (
        <>
          {game.leaderboard && (
            <nav>
              <Tabs value={usedTabIndex} onChange={handleTabChange} variant="fullWidth">
                <Tab label={<FormattedMessage id="game.details.tabs.leaderboard" />} id="tab-0" />
                <Tab label={<FormattedMessage id="game.details.tabs.about" />} id="tab-1" />
              </Tabs>
            </nav>
          )}
          <Box my={6}>
            {game.leaderboard ? (
              <SwipeableViews
                disableLazyLoading
                index={usedTabIndex}
                onChangeIndex={handleTabChangeIndex}
                // This line is a temporary fix https://github.com/oliviertassinari/react-swipeable-views/issues/599
                containerStyle={{
                  transition: 'transform 0.35s cubic-bezier(0.15, 0.3, 0.25, 1) 0s',
                }}
              >
                <TabPanel value={usedTabIndex} index={0}>
                  <Leaderboard gameId={game.id} gameName={game.name} />
                </TabPanel>
                <TabPanel value={usedTabIndex} index={1}>
                  <About
                    recommendedGames={collection.slice(0, 3)}
                    details={mapGameDetails(game)}
                    showTitle={!game.leaderboard}
                  />
                </TabPanel>
              </SwipeableViews>
            ) : (
              <About
                recommendedGames={collection.slice(0, 3)}
                details={mapGameDetails(game)}
                showTitle={!game.leaderboard}
              />
            )}
          </Box>
        </>
      )}
    </>
  );
};

export default GameDetail;
